import { useState } from 'react';
import { Link } from 'react-router-dom';

const QUESTIONS = [
  {
    id: 'budget',
    question: 'Quel est votre budget d\'investissement ?',
    options: [
      { value: 'small', label: 'Moins de 400 000 \u20ac', hint: '~1,5 M\u20aa' },
      { value: 'medium', label: '400 000 \u2013 800 000 \u20ac', hint: '~1,5 \u2013 3 M\u20aa' },
      { value: 'large', label: '800 000 \u2013 1 500 000 \u20ac', hint: '~3 \u2013 5,5 M\u20aa' },
      { value: 'xlarge', label: 'Plus de 1 500 000 \u20ac', hint: '>5,5 M\u20aa' },
    ],
  },
  {
    id: 'objective',
    question: 'Quel est votre objectif principal ?',
    options: [
      { value: 'yield', label: 'Rendement maximal', hint: 'Cash-flow mensuel positif' },
      { value: 'capital', label: 'Préservation du capital', hint: 'Valorisation long terme' },
      { value: 'airbnb', label: 'Location courte durée', hint: 'Airbnb, tourisme' },
      { value: 'mixed', label: 'Usage mixte', hint: 'Résidence + investissement' },
    ],
  },
  {
    id: 'horizon',
    question: 'Quel est votre horizon d\'investissement ?',
    options: [
      { value: 'short', label: '3\u20135 ans', hint: 'Court terme' },
      { value: 'medium', label: '5\u201310 ans', hint: 'Moyen terme' },
      { value: 'long', label: '10\u201320 ans', hint: 'Long terme' },
      { value: 'vlong', label: 'Plus de 20 ans', hint: 'Transmission patrimoniale' },
    ],
  },
  {
    id: 'risk',
    question: 'Quelle est votre tolérance au risque ?',
    options: [
      { value: 'low', label: 'Faible', hint: 'Je privilégie la sécurité' },
      { value: 'medium', label: 'Modérée', hint: 'Équilibre risque/rendement' },
      { value: 'high', label: 'Élevée', hint: 'Je vise le rendement maximum' },
    ],
  },
];

function getRecommendation(answers) {
  const { budget, objective } = answers;

  if (objective === 'airbnb') {
    return {
      city: 'Eilat',
      slug: 'eilat',
      strategy: 'Location courte durée',
      reason: 'Eilat est la capitale du tourisme israélien. Zone franche, taux d\'occupation élevés toute l\'année.',
      yield: '6\u201310 %',
      risk: 'Élevé',
      minBudget: '900 000 \u20aa',
      altCity: 'Tel Aviv (Centre)',
    };
  }
  if (budget === 'small' || objective === 'yield') {
    return {
      city: 'Beer Sheva',
      slug: 'beer-sheva',
      strategy: 'Rendement locatif classique',
      reason: 'Beer Sheva offre les meilleurs rendements bruts (5\u20136 %) avec les tickets d\'entrée les plus accessibles du pays. Ville universitaire en développement.',
      yield: '5\u20136 %',
      risk: 'Moyen',
      minBudget: '500 000 \u20aa',
      altCity: 'Haïfa',
    };
  }
  if (budget === 'medium' && (objective === 'yield' || objective === 'capital')) {
    return {
      city: 'Haïfa',
      slug: 'haifa',
      strategy: 'Équilibre prix / rendement',
      reason: 'Haïfa offre le meilleur équilibre : prix raisonnables (\u201355 % vs Tel Aviv) avec rendement correct (4\u20134,5 %). Ville tech et universitaire, demande stable.',
      yield: '4\u20134,5 %',
      risk: 'Faible',
      minBudget: '850 000 \u20aa',
      altCity: 'Netanya',
    };
  }
  if (budget === 'medium' && objective === 'mixed') {
    return {
      city: 'Netanya',
      slug: 'netanya',
      strategy: 'Usage mixte résidence-investissement',
      reason: 'Netanya est la ville française d\'Israël. Idéale pour un usage mixte : résidence secondaire + location. Communauté francophone, bord de mer.',
      yield: '3,6\u20134,2 %',
      risk: 'Faible',
      minBudget: '1 100 000 \u20aa',
      altCity: 'Herzliya',
    };
  }
  if (budget === 'large' && objective === 'capital') {
    return {
      city: 'Jérusalem',
      slug: 'jerusalem',
      strategy: 'Patrimonial diaspora',
      reason: 'Jérusalem attire une demande internationale permanente de la diaspora mondiale. Bien choisir le quartier (Rechavia, German Colony) est crucial.',
      yield: '2,5\u20133,5 %',
      risk: 'Moyen',
      minBudget: '1 600 000 \u20aa',
      altCity: 'Tel Aviv (Nord)',
    };
  }
  return {
    city: 'Tel Aviv',
    slug: 'tel-aviv',
    strategy: 'Patrimonial premium',
    reason: 'Tel Aviv est le marché le plus liquide d\'Israël. Pour les grands budgets orientés préservation du capital, c\'est la référence incontournable.',
    yield: '2\u20133 %',
    risk: 'Faible',
    minBudget: '2 400 000 \u20aa',
    altCity: 'Herzliya',
  };
}

const RISK_COLORS = {
  'Faible': 'var(--color-success)',
  'Moyen': '#d97706',
  'Élevé': 'var(--color-danger)',
};

export default function InvestorProfile() {
  const [step, setStep] = useState(0); // 0-indexed question index
  const [answers, setAnswers] = useState({});
  const [done, setDone] = useState(false);

  const currentQuestion = QUESTIONS[step];
  const totalSteps = QUESTIONS.length;
  const progress = ((step) / totalSteps) * 100;

  const handleSelect = (value) => {
    const newAnswers = { ...answers, [currentQuestion.id]: value };
    setAnswers(newAnswers);
    if (step < totalSteps - 1) {
      setStep(step + 1);
    } else {
      setDone(true);
    }
  };

  const handleBack = () => {
    if (step > 0) {
      setStep(step - 1);
      setDone(false);
    }
  };

  const handleRestart = () => {
    setStep(0);
    setAnswers({});
    setDone(false);
  };

  const recommendation = done ? getRecommendation(answers) : null;

  return (
    <div className="page-content">
      <div className="container" style={{ maxWidth: 720 }}>
        <span className="gold-tag">Quiz investisseur</span>
        <h1>Quel est votre profil investisseur ?</h1>
        <p style={{ fontSize: '1.05rem', marginBottom: '2.5rem' }}>
          Répondez à 4 questions pour découvrir la ville et la stratégie les mieux adaptées à votre projet.
        </p>

        {!done ? (
          <div style={{ background: 'var(--color-bg-white)', borderRadius: 'var(--radius-lg)', padding: '2.5rem', border: '1px solid var(--color-border)' }}>
            {/* Step indicator + progress */}
            <div style={{ marginBottom: '2rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                <span style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.12em', color: 'var(--color-accent)', fontWeight: 700 }}>
                  Question {step + 1} sur {totalSteps}
                </span>
                <span style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)' }}>
                  {Math.round(((step) / totalSteps) * 100)} % complété
                </span>
              </div>
              <div style={{ height: 4, background: 'var(--color-border)', borderRadius: 2, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${progress}%`, background: 'var(--color-accent)', borderRadius: 2, transition: 'width 0.3s ease' }} />
              </div>
            </div>

            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.35rem', color: 'var(--color-primary)', marginBottom: '1.75rem', lineHeight: 1.4 }}>
              {currentQuestion.question}
            </h2>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {currentQuestion.options.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => handleSelect(opt.value)}
                  style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '1.1rem 1.25rem',
                    background: answers[currentQuestion.id] === opt.value ? 'rgba(196,163,90,0.08)' : 'var(--color-bg)',
                    border: `1.5px solid ${answers[currentQuestion.id] === opt.value ? 'var(--color-accent)' : 'var(--color-border)'}`,
                    borderRadius: 'var(--radius-md)',
                    cursor: 'pointer', textAlign: 'left',
                    transition: 'all 0.15s',
                    fontFamily: 'var(--font-body)',
                  }}
                  onMouseEnter={e => { if (answers[currentQuestion.id] !== opt.value) { e.currentTarget.style.borderColor = 'var(--color-accent)'; e.currentTarget.style.background = 'rgba(196,163,90,0.04)'; } }}
                  onMouseLeave={e => { if (answers[currentQuestion.id] !== opt.value) { e.currentTarget.style.borderColor = 'var(--color-border)'; e.currentTarget.style.background = 'var(--color-bg)'; } }}>
                  <span style={{ fontWeight: 600, fontSize: '0.95rem', color: 'var(--color-primary)' }}>{opt.label}</span>
                  <span style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', marginLeft: '1rem', flexShrink: 0 }}>{opt.hint}</span>
                </button>
              ))}
            </div>

            {step > 0 && (
              <button
                onClick={handleBack}
                style={{ marginTop: '1.5rem', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', fontSize: '0.85rem', fontFamily: 'var(--font-body)', padding: 0 }}>
                Retour à la question précédente
              </button>
            )}
          </div>
        ) : (
          <div>
            {/* Résultat */}
            <div style={{ background: 'var(--color-bg-white)', borderRadius: 'var(--radius-lg)', padding: '2.5rem', border: '1px solid var(--color-border)', borderTop: '3px solid var(--color-accent)' }}>
              <div style={{ marginBottom: '0.5rem' }}>
                <span style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.12em', color: 'var(--color-accent)', fontWeight: 700 }}>
                  Votre recommandation
                </span>
              </div>
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '2rem', color: 'var(--color-primary)', margin: '0.25rem 0 0.125rem' }}>
                {recommendation.city}
              </h2>
              <div style={{ fontSize: '1rem', color: 'var(--color-text-muted)', marginBottom: '1.75rem' }}>
                Stratégie recommandée : <strong style={{ color: 'var(--color-primary)' }}>{recommendation.strategy}</strong>
              </div>

              <p style={{ fontSize: '0.95rem', lineHeight: 1.8, color: 'var(--color-text)', marginBottom: '2rem', padding: '1.25rem', background: 'var(--color-bg)', borderRadius: 'var(--radius-md)', borderLeft: '3px solid var(--color-accent)' }}>
                {recommendation.reason}
              </p>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '2rem' }}>
                <div style={{ padding: '1rem', background: 'var(--color-bg)', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)' }}>
                  <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--color-text-muted)', marginBottom: 6 }}>Rendement estimé</div>
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.35rem', fontWeight: 700, color: 'var(--color-success)' }}>{recommendation.yield}</div>
                </div>
                <div style={{ padding: '1rem', background: 'var(--color-bg)', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)' }}>
                  <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--color-text-muted)', marginBottom: 6 }}>Niveau de risque</div>
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.35rem', fontWeight: 700, color: RISK_COLORS[recommendation.risk] || 'var(--color-primary)' }}>{recommendation.risk}</div>
                </div>
                <div style={{ padding: '1rem', background: 'var(--color-bg)', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)' }}>
                  <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--color-text-muted)', marginBottom: 6 }}>Budget minimal</div>
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.15rem', fontWeight: 700, color: 'var(--color-primary)' }}>{recommendation.minBudget}</div>
                </div>
              </div>

              <div style={{ fontSize: '0.82rem', color: 'var(--color-text-muted)', marginBottom: '2rem' }}>
                Ville alternative à considérer : <strong style={{ color: 'var(--color-primary)' }}>{recommendation.altCity}</strong>
              </div>

              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                <Link to={`/villes/${recommendation.slug}`} className="btn btn-primary">
                  Voir la fiche {recommendation.city}
                </Link>
                <Link to="/accompagnement" className="btn btn-accent">
                  Être accompagné
                </Link>
                <button
                  onClick={handleRestart}
                  style={{ background: 'none', border: '1.5px solid var(--color-border)', borderRadius: 'var(--radius-sm)', padding: '0.5rem 1.25rem', cursor: 'pointer', fontSize: '0.875rem', color: 'var(--color-text-muted)', fontFamily: 'var(--font-body)' }}>
                  Recommencer le quiz
                </button>
              </div>
            </div>

            <div className="insight-box" style={{ marginTop: '1.5rem' }}>
              Cette recommandation est basée sur vos réponses et les données de marché 2024–2025. Elle est indicative et ne constitue pas un conseil en investissement.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
